// Match Timing Utilities
//
// This module provides functionality for tracking match durations,
// estimating completion times, and identifying slow matches.

/**
 * Get estimated match duration in minutes based on tournament settings
 * @param {Object} tournament - Tournament configuration
 * @param {number} tournament.expectedTimePerMatch - Expected match time in minutes (default: 30)
 * @returns {number} Estimated duration in minutes
 */
export const getEstimatedMatchDuration = (tournament) => {
  // Use the host-configured expected match time directly
  const { expectedTimePerMatch = 30 } = tournament;

  return expectedTimePerMatch;
};

/**
 * Calculate the estimated end time for a match
 * @param {Object} match - Match object
 * @param {number|null} match.actualStartTime - Timestamp when match started
 * @param {Object} tournament - Tournament configuration
 * @returns {Date|null} Estimated end time or null if match hasn't started
 */
export const calculateMatchEndTime = (match, tournament) => {
  if (!match.started || !match.actualStartTime) {
    return null;
  }

  const duration = getEstimatedMatchDuration(tournament);
  const startTime = new Date(match.actualStartTime);
  const endTime = new Date(startTime.getTime() + (duration * 60000));

  return endTime;
};

/**
 * Get elapsed time for a match in minutes
 * @param {Object} match - Match object
 * @returns {number|null} Elapsed time in minutes or null if not started
 */
export const getMatchElapsedTime = (match) => {
  if (!match.started || !match.actualStartTime) {
    return null;
  }

  const now = new Date();
  const startTime = new Date(match.actualStartTime);
  const elapsedMs = now - startTime;
  const elapsedMinutes = elapsedMs / 60000;

  return elapsedMinutes;
};

/**
 * Check if a match is running slow (over estimated time)
 * @param {Object} match - Match object
 * @param {Object} tournament - Tournament configuration
 * @param {number} threshold - Percentage over estimate to trigger (default 1.25 = 125%)
 * @returns {Object|null} Slow match info or null if not slow
 */
export const isMatchSlow = (match, tournament, threshold = 1.25) => {
  if (!match.started || match.finished || !match.actualStartTime) {
    return null;
  }

  const estimatedDuration = getEstimatedMatchDuration(tournament);
  const elapsedMinutes = getMatchElapsedTime(match);

  if (!elapsedMinutes) {
    return null;
  }

  // Check if elapsed time exceeds threshold
  if (elapsedMinutes > estimatedDuration * threshold) {
    return {
      match,
      elapsedMinutes: Math.round(elapsedMinutes),
      estimatedMinutes: estimatedDuration,
      overBy: Math.round(elapsedMinutes - estimatedDuration),
      percentOver: Math.round(((elapsedMinutes - estimatedDuration) / estimatedDuration) * 100)
    };
  }

  return null;
};

/**
 * Get all slow matches in a bracket
 * @param {Object[]} bracket - Array of tournament matches
 * @param {Object} tournament - Tournament configuration
 * @param {number} threshold - Percentage over estimate to trigger (default 1.25 = 125%)
 * @returns {Object[]} Array of slow match information objects
 */
export const getSlowMatches = (bracket, tournament, threshold = 1.25) => {
  const slowMatches = [];

  bracket.forEach(match => {
    const slowInfo = isMatchSlow(match, tournament, threshold);
    if (slowInfo) {
      slowMatches.push(slowInfo);
    }
  });

  // Sort by how far over time (most over first)
  slowMatches.sort((a, b) => b.percentOver - a.percentOver);

  return slowMatches;
};

/**
 * Get alert level for a match based on how far over time it is
 * @param {Object} match - Match object
 * @param {Object} tournament - Tournament configuration
 * @returns {Object|null} Alert level info or null if within acceptable range
 */
export const getMatchAlertLevel = (match, tournament) => {
  if (!match.started || match.finished || !match.actualStartTime) {
    return null;
  }

  const estimatedDuration = getEstimatedMatchDuration(tournament);
  const elapsedMinutes = getMatchElapsedTime(match);

  if (!elapsedMinutes) {
    return null;
  }

  const percentOver = ((elapsedMinutes - estimatedDuration) / estimatedDuration) * 100;

  // Within acceptable range (up to 25% over)
  if (percentOver < 25) {
    return null;
  }

  // Warning level (25-50% over)
  if (percentOver < 50) {
    return {
      level: 'warning',
      color: '#ffc107',
      backgroundColor: '#fff3cd',
      icon: '⚡',
      message: 'Running slightly over time'
    };
  }

  // Slow level (50-100% over)
  if (percentOver < 100) {
    return {
      level: 'slow',
      color: '#ff9800',
      backgroundColor: '#ffe0b2',
      icon: '⚠️',
      message: 'Running significantly over time'
    };
  }

  // Critical level (100%+ over - taking twice as long)
  return {
    level: 'critical',
    color: '#f44336',
    backgroundColor: '#ffcdd2',
    icon: '🚨',
    message: 'Critical delay - check on this match'
  };
};

/**
 * Format minutes into a readable time string
 * @param {number} minutes - Number of minutes
 * @returns {string} Formatted time string (e.g., "15 min" or "1h 15m")
 */
export const formatMinutes = (minutes) => {
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}m`;
};

/**
 * Format a timestamp into a readable time string
 * @param {number|Date} timestamp - Timestamp or Date object
 * @returns {string} Formatted time string (e.g., "2:30 PM")
 */
export const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get summary statistics for all active matches
 * @param {Object[]} bracket - Array of tournament matches
 * @param {Object} tournament - Tournament configuration
 * @returns {Object} Summary statistics
 */
export const getMatchTimingStats = (bracket, tournament) => {
  const activeMatches = bracket.filter(m => m.started && !m.finished);
  const slowMatches = getSlowMatches(bracket, tournament);

  let totalElapsedTime = 0;
  let longestMatch = null;
  let longestDuration = 0;

  activeMatches.forEach(match => {
    const elapsed = getMatchElapsedTime(match);
    if (elapsed) {
      totalElapsedTime += elapsed;

      if (elapsed > longestDuration) {
        longestDuration = elapsed;
        longestMatch = match;
      }
    }
  });

  const avgElapsedTime = activeMatches.length > 0
    ? totalElapsedTime / activeMatches.length
    : 0;

  return {
    activeMatches: activeMatches.length,
    slowMatches: slowMatches.length,
    avgElapsedTime: Math.round(avgElapsedTime),
    longestMatch,
    longestDuration: Math.round(longestDuration)
  };
};
